import { useEffect, useState, type JSX } from "react";
import { Button } from "../../../components";
import type { ReviewStepCurrent } from "../../../data/chatQuery";
import { ClauseDiffLine } from "./ClauseDiffLine";
import { ReviewActions } from "./ReviewActions";
import { ReviewTypeBox } from "./ReviewTypeBox";
import { ReviewWhyDialog } from "./ReviewWhyDialog";
import type { ReviewCardProps } from "./reviewCardProps";

const RISK_LABEL = { high: "High risk", medium: "Medium risk", low: "Low risk" } as const;

// The card's ONE why line: a plain-language sentence when the backend supplied
// one, else the engine's `why`; for an additional clause, its first conflict or
// the standing note.
export function reviewWhyLine(cur: ReviewStepCurrent): string {
  const reco = cur.recommendation;
  if (reco !== null) {
    const plain = reco.whyPlain;
    return plain != null && plain.trim().length > 0 ? plain.trim() : reco.why;
  }
  const first = cur.conflicts[0];
  if (first !== undefined) {
    const more = cur.conflicts.length - 1;
    return `Conflicts with ${first.standardClauseName}: ${first.reason}${
      more > 0 ? ` (+${more} more)` : ""
    }`;
  }
  if (cur.outlier != null) return cur.outlier.summary;
  if (cur.category === "additional") {
    return "Additional clause — the certified standard doesn’t cover this. That’s fine on its own; keep it or decide as you like.";
  }
  return "";
}

function MagnifierIcon(): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="2.2" />
      <path d="M15.5 15.5L20 20" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

// [review-compact] The redesigned open card (docs/Wireframes/inline-review-redesign):
// progress → title → clause with inline diff → one why line (+ magnifier) → actions.
// Evidence lives in the Why dialog. Same review state and decide/goto calls as the
// classic card.
export function CompactReviewCard({
  current: cur,
  pending,
  decision,
  onDecide,
  onGoto,
  onTypeSubmit,
  onJumpToDraft,
  revising = false,
  progress,
}: ReviewCardProps): JSX.Element {
  const [typing, setTyping] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  useEffect(() => {
    setTyping(false);
    setWhyOpen(false);
  }, [cur.index]);

  const displayNumber = cur.displayNumber ?? null;
  const why = reviewWhyLine(cur);
  const walked = progress?.steps.filter((s) => s.needsReview) ?? [];
  const position = walked.findIndex((s) => s.index === cur.index) + 1;

  return (
    <div className="sa-cr" data-testid="review-card" data-category={cur.category}>
      {progress !== undefined && (
        <div className="sa-cr__progress" data-testid="review-progress">
          <span className="sa-cr__pos">
            Review {position > 0 ? position : "–"} of {progress.total}
          </span>
          <span className="sa-cr__bar" role="list">
            {walked.map((s) => {
              const state =
                s.index === cur.index
                  ? "current"
                  : s.decision === "pending"
                    ? "pending"
                    : s.decision;
              return (
                <Button
                  key={s.index}
                  variant="bare"
                  role="listitem"
                  className={`sa-cr__seg is-${state}`}
                  title={s.shortName ?? s.name}
                  aria-label={s.shortName ?? s.name}
                  disabled={pending || state === "pending" || state === "current"}
                  onClick={() => onGoto(s.index)}
                >
                  <span aria-hidden="true" />
                </Button>
              );
            })}
          </span>
          <span className="sa-cr__counts">
            <span>
              <i className="is-applied" aria-hidden="true" />
              {progress.counts.applied} applied
            </span>
            <span>
              <i className="is-kept" aria-hidden="true" />
              {progress.counts.kept} kept
            </span>
            <span>{progress.counts.left} left</span>
          </span>
        </div>
      )}

      <div className="sa-cr__title">
        {displayNumber !== null && displayNumber.length > 0 && (
          <span className="sa-cr__no">§ {displayNumber}</span>
        )}
        {/* [review-jump 2026-10-15] Bill Jacks: "if I click on this, does it show
            me where in the draft? … Not if I click on it." The jump lived only
            inside the details dialog; the clause name is now the way there. */}
        {onJumpToDraft ? (
          <Button
            variant="bare"
            className="sa-cr__name sa-cr__name--jump"
            title="Show this clause in the draft"
            data-testid="review-jump-to-draft"
            onClick={onJumpToDraft}
          >
            <span data-testid="review-clause-name">{cur.name}</span>
            <span className="sa-cr__jump" aria-hidden="true">
              ↗
            </span>
          </Button>
        ) : (
          <span className="sa-cr__name" data-testid="review-clause-name">
            {cur.name}
          </span>
        )}
        {cur.category === "additional" && (
          <span className="sa-cr__addl" data-testid="review-category">
            Additional
          </span>
        )}
        {cur.category === "missing" && (
          <span className="sa-cr__addl" data-testid="review-category">
            Missing
          </span>
        )}
        <span className={`sa-rev__risk is-${cur.risk}`}>{RISK_LABEL[cur.risk]}</span>
      </div>

      <div className="sa-cr__clause" data-testid="review-clause-line">
        <ClauseDiffLine cur={cur} />
      </div>

      <div className="sa-cr__why" onDoubleClick={() => setWhyOpen(true)}>
        <Button
          variant="bare"
          className="sa-cr__mag"
          title="See full details"
          aria-label="See full details"
          data-testid="review-why-open"
          onClick={() => setWhyOpen(true)}
        >
          <MagnifierIcon />
        </Button>
        {why.length > 0 && (
          <span className="sa-cr__why-txt" data-testid="review-reco-paragraph">
            {why}
          </span>
        )}
      </div>

      {revising && !whyOpen && (
        <div className="sa-rev__revising sa-cr__pad" data-testid="review-revising">
          Revising the recommendation…
        </div>
      )}

      <div className="sa-cr__acts">
        <ReviewActions
          index={cur.index}
          pending={pending}
          onDecide={onDecide}
          onType={() => setTyping(true)}
          canApply={cur.recommendation !== null}
          applied={decision === "applied"}
          decisionNote={cur.decisionNote}
        />
        {typing && (
          <ReviewTypeBox
            key={cur.index}
            clauseName={cur.name}
            pending={pending}
            onSubmit={(text) => {
              onTypeSubmit(cur.index, cur.name, text);
              setTyping(false);
            }}
            onCancel={() => setTyping(false)}
          />
        )}
      </div>

      <ReviewWhyDialog
        cur={cur}
        open={whyOpen}
        onOpenChange={setWhyOpen}
        revising={revising}
        {...(onJumpToDraft ? { onJumpToDraft } : {})}
      />
    </div>
  );
}
